/* TimeFeedPage — TikTok-style vertical feed of dramatic historical micro-stories.
 *
 * Each clip: full-screen cinematic image with bold hook overlay + slow Ken Burns zoom
 * + Swedish voice-over (Web Speech API) + facts.
 *
 * Swipe up = next, swipe down = previous (history), tap = pause/play voice.
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Heart, ChevronUp, ChevronDown, Pause, Play, Sparkles, Volume2, VolumeX, Search, X, Loader2, ArrowLeft, Video, Film, Theater, Rocket, BookOpen } from 'lucide-react';
import { toast } from 'sonner';
import LlmBudgetBanner from './LlmBudgetBanner';
import TimeCapsuleModal from './TimeCapsuleModal';
import TimeImmerseModal from './TimeImmerseModal';
import TimeSimulatorModal from './TimeSimulatorModal';
import TimeHistoryBookModal from './TimeHistoryBookModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function speak(text) {
  if (!text) return null;
  try {
    window.speechSynthesis?.cancel();
    const u = new SpeechSynthesisUtterance(text);
    u.lang = 'sv-SE';
    u.rate = 0.95;
    u.pitch = 1.0;
    u.volume = 1.0;
    const voices = window.speechSynthesis.getVoices?.() || [];
    const sv = voices.find((v) => v.lang?.startsWith('sv'));
    if (sv) u.voice = sv;
    window.speechSynthesis.speak(u);
    return u;
  } catch {
    return null;
  }
}

function stopSpeaking() {
  try { window.speechSynthesis?.cancel(); } catch {/**/}
}

// =============== TOPIC PICKER MODAL ===============
function TopicPicker({ open, onClose, onPick, topics }) {
  const [customTopic, setCustomTopic] = useState('');
  const [busy, setBusy] = useState(false);
  if (!open) return null;

  const submitCustom = async () => {
    if (!customTopic.trim()) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/timefeed/custom`, { topic: customTopic.trim() }, { withCredentials: true });
      onPick({ custom: r.data?.clip });
      onClose();
    } catch (e) {
      toast.error('Kunde inte skapa: ' + (e?.response?.data?.detail || ''));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[350] bg-black/70 backdrop-blur flex items-center justify-center p-4" onClick={onClose} data-testid="timefeed-topic-picker">
      <div className="bg-gradient-to-br from-amber-50 via-rose-50 to-violet-50 rounded-3xl max-w-md w-full shadow-2xl border-2 border-amber-200 max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 px-5 py-3.5 bg-gradient-to-r from-amber-100 to-rose-100 border-b border-amber-200 rounded-t-3xl flex items-center justify-between">
          <h2 className="font-bold text-amber-900">🕰️ Vad vill du resa till?</h2>
          <button onClick={onClose} className="p-1.5 rounded-full hover:bg-white/60"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <p className="text-xs font-bold uppercase tracking-wide text-amber-700/70 mb-2">Skriv ditt eget ämne</p>
            <div className="flex gap-2">
              <input
                value={customTopic}
                onChange={(e) => setCustomTopic(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && submitCustom()}
                placeholder='t.ex. "kvinnliga vetenskapsmän" eller "1500-talets pirater"'
                data-testid="timefeed-custom-input"
                className="flex-1 px-3 py-2 rounded-xl border-2 border-amber-200 focus:border-amber-500 outline-none text-sm"
                maxLength={200}
              />
              <button
                onClick={submitCustom}
                disabled={busy || !customTopic.trim()}
                data-testid="timefeed-custom-submit"
                className="px-4 py-2 rounded-xl bg-gradient-to-r from-amber-500 to-rose-500 text-white font-bold text-sm shadow disabled:opacity-50 flex items-center gap-1"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                Skapa
              </button>
            </div>
            <p className="text-[10px] text-amber-700/60 italic mt-1">AI bygger ett unikt klipp ~20 sek</p>
          </div>
          <div>
            <p className="text-xs font-bold uppercase tracking-wide text-amber-700/70 mb-2">Eller välj tema</p>
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => { onPick({ topic: null }); onClose(); }}
                data-testid="timefeed-topic-any"
                className="py-3 rounded-xl bg-white hover:bg-amber-50 border-2 border-amber-200 text-sm font-bold text-amber-800"
              >
                🎲 Alla teman
              </button>
              {topics.map((t) => (
                <button
                  key={t.key}
                  onClick={() => { onPick({ topic: t.key }); onClose(); }}
                  data-testid={`timefeed-topic-${t.key}`}
                  className="py-3 rounded-xl bg-white hover:bg-amber-50 border-2 border-amber-200 text-sm font-bold text-gray-700 text-left px-3"
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// =============== FACTS DRAWER ===============
function FactsDrawer({ open, clip, onClose }) {
  if (!open || !clip) return null;
  return (
    <div className="fixed inset-0 z-[340] bg-black/60 backdrop-blur flex items-end sm:items-center justify-center p-3 sm:p-6" onClick={onClose} data-testid="timefeed-facts-drawer">
      <div className="bg-gradient-to-br from-amber-50 to-rose-50 rounded-t-3xl sm:rounded-3xl max-w-md w-full shadow-2xl border-2 border-amber-200" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3 bg-gradient-to-r from-amber-100 to-rose-100 border-b border-amber-200 rounded-t-3xl flex items-center justify-between">
          <h3 className="font-bold text-amber-900 text-sm">{clip.year} · {clip.place}</h3>
          <button onClick={onClose} className="p-1 rounded-full hover:bg-white/60"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 space-y-3">
          <p className="text-base text-gray-700 italic leading-relaxed">"{clip.voice_script}"</p>
          {clip.facts && clip.facts.length > 0 && (
            <div className="space-y-1.5 border-t border-amber-100 pt-3">
              <p className="text-[10px] uppercase font-bold tracking-wide text-amber-700/70">Fakta</p>
              {clip.facts.map((f, i) => (
                <p key={i} className="text-sm text-gray-700 bg-white/70 rounded-lg px-3 py-2">{f}</p>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// =============== ONE CLIP RENDER ===============
function ClipCard({ clip, isActive, voiceOn, onLike, onUpgradeVideo, onVideoReady, onImmerse, onSimulate }) {
  const utterRef = useRef(null);
  const [speaking, setSpeaking] = useState(false);
  const [videoStatus, setVideoStatus] = useState(clip?.video?.status || 'none');
  const [videoB64, setVideoB64] = useState(clip?.video?.video_base64 || null);
  const [videoStartedAt, setVideoStartedAt] = useState(null);
  const [videoElapsedSec, setVideoElapsedSec] = useState(0);
  const pollRef = useRef(null);

  // Reset when clip changes
  useEffect(() => {
    setVideoStatus(clip?.video?.status || 'none');
    setVideoB64(clip?.video?.video_base64 || null);
    setVideoStartedAt(clip?.video?.status === 'generating' ? Date.now() : null);
    setVideoElapsedSec(0);
  }, [clip?.id, clip?.video?.status, clip?.video?.video_base64]);

  // Elapsed counter while building
  useEffect(() => {
    if (videoStatus !== 'generating' || !videoStartedAt) return;
    const id = setInterval(() => {
      setVideoElapsedSec(Math.floor((Date.now() - videoStartedAt) / 1000));
    }, 1000);
    return () => clearInterval(id);
  }, [videoStatus, videoStartedAt]);

  // Poll video status while generating
  useEffect(() => {
    if (videoStatus !== 'generating' || !clip?.id) return;
    let cancelled = false;
    const poll = async () => {
      try {
        const r = await axios.get(`${API}/timefeed/clip/${clip.id}/video-status`, { withCredentials: true });
        if (cancelled) return;
        const s = r.data?.status;
        setVideoStatus(s || 'none');
        if (s === 'ready') {
          setVideoB64(r.data.video_base64);
          toast.success('🎬 Video klar!');
          onVideoReady?.(clip.id, r.data);
        } else if (s === 'failed') {
          toast.error('💔 Videon kunde inte skapas — försök igen senare (kan vara LLM-saldot)');
          clearInterval(pollRef.current);
        }
      } catch {/**/}
    };
    pollRef.current = setInterval(poll, 8000);
    return () => { cancelled = true; clearInterval(pollRef.current); };
  }, [videoStatus, clip?.id, onVideoReady]);

  // Speak when becomes active (only when no video — video has its own audio? No, Sora 2 is silent — keep narration)
  useEffect(() => {
    stopSpeaking();
    setSpeaking(false);
    if (!isActive || !voiceOn || !clip?.voice_script) return;
    const t = setTimeout(() => {
      const u = speak(clip.voice_script);
      utterRef.current = u;
      setSpeaking(true);
      if (u) {
        u.onend = () => setSpeaking(false);
        u.onerror = () => setSpeaking(false);
      }
    }, 350);
    return () => { clearTimeout(t); stopSpeaking(); setSpeaking(false); };
  }, [isActive, voiceOn, clip?.voice_script, clip?.id]);

  const togglePause = (e) => {
    e?.stopPropagation();
    const synth = window.speechSynthesis;
    if (!synth) return;
    if (speaking && !synth.paused) {
      synth.pause();
      setSpeaking(false);
    } else if (synth.paused) {
      synth.resume();
      setSpeaking(true);
    } else if (clip?.voice_script) {
      const u = speak(clip.voice_script);
      utterRef.current = u;
      setSpeaking(true);
      if (u) {
        u.onend = () => setSpeaking(false);
        u.onerror = () => setSpeaking(false);
      }
    }
  };

  const triggerUpgrade = async (e) => {
    e?.stopPropagation();
    if (videoStatus === 'generating') return;
    setVideoStatus('generating');
    setVideoStartedAt(Date.now());
    setVideoElapsedSec(0);
    try {
      await axios.post(`${API}/timefeed/clip/${clip.id}/upgrade-video`, {}, { withCredentials: true });
      toast.info('🎬 Bygger video… (~1-2 min)', { duration: 5000 });
    } catch (e) {
      setVideoStatus('none');
      setVideoStartedAt(null);
      toast.error('Kunde inte starta: ' + (e?.response?.data?.detail || ''));
    }
  };

  if (!clip) return null;
  const bg = clip.image_base64
    ? `url("data:${clip.image_mime || 'image/png'};base64,${clip.image_base64}")`
    : 'linear-gradient(135deg,#1a1a2e,#16213e,#0f3460)';

  return (
    <div
      className="relative w-full h-full flex items-end justify-start overflow-hidden bg-black"
      data-testid={`timefeed-clip-${clip.id}`}
    >
      {/* Video if ready, else image with Ken Burns */}
      {videoB64 ? (
        <video
          src={`data:video/mp4;base64,${videoB64}`}
          autoPlay loop muted playsInline
          data-testid={`timefeed-video-${clip.id}`}
          className="absolute inset-0 w-full h-full object-cover"
        />
      ) : (
        <div
          className="absolute inset-0 transition-transform"
          style={{
            backgroundImage: bg,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            animation: isActive ? 'kenburns 22s ease-in-out infinite alternate' : 'none',
          }}
        />
      )}
      {/* Bottom gradient for text legibility */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent" />
      {/* Vignette */}
      <div className="absolute inset-0" style={{ background: 'radial-gradient(circle at center, transparent 35%, rgba(0,0,0,0.6) 100%)' }} />

      {/* Top bar: topic + year */}
      <div className="absolute top-3 left-3 right-3 flex items-center justify-between text-white">
        <span className="text-[11px] bg-white/15 backdrop-blur px-2.5 py-1 rounded-full font-bold uppercase tracking-wider">
          {clip.topic_label}
        </span>
        <div className="flex items-center gap-1.5">
          {videoB64 && (
            <span className="text-[10px] bg-rose-500/80 backdrop-blur px-2 py-0.5 rounded-full font-bold uppercase flex items-center gap-1">
              <Film className="w-3 h-3" /> Video
            </span>
          )}
          <span className="text-[11px] bg-black/40 backdrop-blur px-2.5 py-1 rounded-full font-mono">
            {clip.year}
          </span>
        </div>
      </div>

      {/* Persistent "Bygger video…" banner while Sora 2 cooks */}
      {videoStatus === 'generating' && !videoB64 && (
        <div
          className="absolute top-14 left-3 right-3 z-20 flex items-center justify-center"
          data-testid={`timefeed-video-banner-${clip.id}`}
        >
          <div className="bg-violet-600/85 backdrop-blur px-3 py-2 rounded-2xl shadow-lg border border-violet-400/50 flex items-center gap-2 text-white">
            <Loader2 className="w-4 h-4 animate-spin" />
            <div className="flex flex-col leading-tight">
              <span className="text-[11px] font-black uppercase tracking-wide">🎬 Bygger video…</span>
              <span className="text-[10px] text-violet-100/90">
                ~1–2 min · {Math.floor(videoElapsedSec / 60)}:{String(videoElapsedSec % 60).padStart(2, '0')} förflutet
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Main hook + subtitle (bottom-left like TikTok) */}
      <div className="relative z-10 p-5 sm:p-6 text-white space-y-1 max-w-2xl">
        <p className="text-[11px] font-bold uppercase tracking-wider text-amber-300">{clip.place}</p>
        <h1
          className="text-3xl sm:text-5xl font-black leading-tight uppercase tracking-tight"
          style={{ textShadow: '0 4px 24px rgba(0,0,0,0.85), 0 0 8px rgba(0,0,0,0.6)' }}
        >
          {clip.hook}
        </h1>
        <p
          className="text-sm sm:text-base text-white/90 font-medium pt-1 max-w-xl"
          style={{ textShadow: '0 2px 12px rgba(0,0,0,0.85)' }}
        >
          {clip.subtitle}
        </p>
        {speaking && (
          <div className="flex items-center gap-1.5 text-[10px] text-amber-200 pt-2 animate-pulse">
            <Volume2 className="w-3 h-3" /> Berättar…
          </div>
        )}
      </div>

      {/* Right side controls (like TikTok) */}
      <div className="absolute right-3 bottom-32 sm:bottom-28 flex flex-col gap-3 items-center z-10">
        <button
          onClick={(e) => { e.stopPropagation(); onLike(); }}
          data-testid={`timefeed-like-${clip.id}`}
          className="flex flex-col items-center gap-0.5"
        >
          <div className={`p-2.5 rounded-full backdrop-blur transition ${clip.liked ? 'bg-rose-500' : 'bg-white/20 hover:bg-white/30'}`}>
            <Heart className={`w-6 h-6 text-white ${clip.liked ? 'fill-white' : ''}`} />
          </div>
          <span className="text-[10px] text-white font-bold">{clip.liked ? 'Sparad' : 'Gilla'}</span>
        </button>
        <button
          onClick={togglePause}
          data-testid={`timefeed-pause-${clip.id}`}
          className="flex flex-col items-center gap-0.5"
        >
          <div className="p-2.5 rounded-full bg-white/20 hover:bg-white/30 backdrop-blur">
            {speaking ? <Pause className="w-6 h-6 text-white" /> : <Play className="w-6 h-6 text-white fill-white" />}
          </div>
          <span className="text-[10px] text-white font-bold">{speaking ? 'Paus' : 'Spela'}</span>
        </button>
        {/* Upgrade to video button */}
        <button
          onClick={triggerUpgrade}
          disabled={videoStatus === 'generating' || !!videoB64}
          data-testid={`timefeed-upgrade-${clip.id}`}
          className="flex flex-col items-center gap-0.5 disabled:opacity-50"
        >
          <div className={`p-2.5 rounded-full backdrop-blur transition ${
            videoB64 ? 'bg-emerald-500' :
            videoStatus === 'generating' ? 'bg-amber-500 animate-pulse' :
            videoStatus === 'failed' ? 'bg-rose-500 hover:bg-rose-600' :
            'bg-violet-500/80 hover:bg-violet-500'
          }`}>
            {videoStatus === 'generating'
              ? <Loader2 className="w-6 h-6 text-white animate-spin" />
              : <Video className="w-6 h-6 text-white" />}
          </div>
          <span className="text-[10px] text-white font-bold">
            {videoB64 ? 'Klar' :
             videoStatus === 'generating' ? 'Bygger…' :
             videoStatus === 'failed' ? 'Försök igen' : 'Video'}
          </span>
        </button>

        {/* 🎭 Hoppa in i scenen */}
        <button
          onClick={(e) => { e.stopPropagation(); onImmerse?.(clip); }}
          data-testid={`timefeed-immerse-${clip.id}`}
          className="flex flex-col items-center gap-0.5"
        >
          <div className="p-2.5 rounded-full backdrop-blur bg-amber-500/85 hover:bg-amber-500 transition">
            <Theater className="w-6 h-6 text-white" />
          </div>
          <span className="text-[10px] text-white font-bold">Hoppa in</span>
        </button>

        {/* 🚀 Första-person-simulator */}
        <button
          onClick={(e) => { e.stopPropagation(); onSimulate?.(clip); }}
          data-testid={`timefeed-simulate-${clip.id}`}
          className="flex flex-col items-center gap-0.5"
        >
          <div className="p-2.5 rounded-full backdrop-blur bg-violet-600/85 hover:bg-violet-600 transition">
            <Rocket className="w-6 h-6 text-white" />
          </div>
          <span className="text-[10px] text-white font-bold">Simulator</span>
        </button>
      </div>
    </div>
  );
}

// =============== MAIN PAGE ===============
export default function TimeFeedPage() {
  const [clips, setClips] = useState([]);
  const [idx, setIdx] = useState(0);
  const [loadingNext, setLoadingNext] = useState(false);
  const [topics, setTopics] = useState([]);
  const [topicFilter, setTopicFilter] = useState(null);
  const [voiceOn, setVoiceOn] = useState(true);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [capsuleOpen, setCapsuleOpen] = useState(false);
  const [factsOpen, setFactsOpen] = useState(false);
  const [showLiked, setShowLiked] = useState(false);
  const [immerseClipId, setImmerseClipId] = useState(null);
  const [simClipId, setSimClipId] = useState(null);
  const [historyBookOpen, setHistoryBookOpen] = useState(false);
  const [likedClips, setLikedClips] = useState([]);
  const [upgradeAllBusy, setUpgradeAllBusy] = useState(false);
  const [upgradeAllStatus, setUpgradeAllStatus] = useState(null);
  const upgradeAllPollRef = useRef(null);
  const touchStartY = useRef(null);
  const containerRef = useRef(null);

  // Load topics
  useEffect(() => {
    axios.get(`${API}/timefeed/topics`, { withCredentials: true })
      .then((r) => setTopics(r.data?.topics || []))
      .catch(() => {/**/});
  }, []);

  // Pre-load voices for Swedish
  useEffect(() => {
    try { window.speechSynthesis?.getVoices?.(); } catch {/**/}
  }, []);

  const fetchNext = useCallback(async (override) => {
    setLoadingNext(true);
    try {
      const exclude = clips.map((c) => c.id).join(',');
      const t = override?.topic !== undefined ? override.topic : topicFilter;
      let clip;
      if (override?.custom) {
        clip = override.custom;
      } else {
        const r = await axios.get(`${API}/timefeed/next`, {
          params: { topic: t || undefined, exclude: exclude || undefined },
          withCredentials: true,
          timeout: 60000,
        });
        clip = r.data?.clip;
      }
      if (clip) {
        setClips((c) => [...c, clip]);
      }
    } catch (e) {
      toast.error('Kunde inte hämta nästa klipp 💔 ' + (e?.response?.data?.detail || ''));
    } finally {
      setLoadingNext(false);
    }
  }, [clips, topicFilter]);

  // Initial load
  useEffect(() => {
    if (clips.length === 0 && !loadingNext) {
      fetchNext();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Pre-fetch next clip when user lands on the current one
  useEffect(() => {
    if (idx === clips.length - 1 && clips.length > 0 && !loadingNext) {
      fetchNext();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idx, clips.length]);

  const goNext = () => {
    if (idx < clips.length - 1) {
      setIdx(idx + 1);
    } else if (!loadingNext) {
      fetchNext();
    }
  };
  const goPrev = () => idx > 0 && setIdx(idx - 1);

  // Touch swipe
  const onTouchStart = (e) => { touchStartY.current = e.touches[0].clientY; };
  const onTouchEnd = (e) => {
    if (touchStartY.current == null) return;
    const diff = touchStartY.current - e.changedTouches[0].clientY;
    if (Math.abs(diff) > 50) {
      if (diff > 0) goNext(); else goPrev();
    }
    touchStartY.current = null;
  };
  const onWheel = (e) => {
    if (e.deltaY > 30) { e.preventDefault(); goNext(); }
    else if (e.deltaY < -30) { e.preventDefault(); goPrev(); }
  };

  // Keyboard
  useEffect(() => {
    const onKey = (e) => {
      if (factsOpen || pickerOpen || showLiked) return;
      if (e.key === 'ArrowDown' || e.key === ' ') { e.preventDefault(); goNext(); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); goPrev(); }
      else if (e.key === 'Escape') stopSpeaking();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idx, clips.length, loadingNext, factsOpen, pickerOpen, showLiked]);

  // Stop voice on unmount
  useEffect(() => () => { stopSpeaking(); if (upgradeAllPollRef.current) clearInterval(upgradeAllPollRef.current); }, []);

  const toggleLike = async () => {
    const current = clips[idx];
    if (!current) return;
    try {
      const r = await axios.post(`${API}/timefeed/clip/${current.id}/like`, {}, { withCredentials: true });
      const liked = r.data?.liked;
      setClips((cs) => cs.map((c, i) => i === idx ? { ...c, liked } : c));
      toast.success(liked ? '💖 Sparat' : '🤍 Borttagen');
    } catch { toast.error('Kunde inte spara'); }
  };

  const loadLiked = async () => {
    try {
      const r = await axios.get(`${API}/timefeed/liked`, { withCredentials: true });
      setLikedClips(r.data?.items || []);
      setShowLiked(true);
      // Also fetch status
      try {
        const s = await axios.get(`${API}/timefeed/upgrade-all-status`, { withCredentials: true });
        setUpgradeAllStatus(s.data);
      } catch {/**/}
    } catch { toast.error('Kunde inte ladda sparade'); }
  };

  const upgradeAllLiked = async () => {
    setUpgradeAllBusy(true);
    try {
      const r = await axios.post(`${API}/timefeed/upgrade-all-liked`, {}, { withCredentials: true });
      const queued = r.data?.queued || 0;
      const skipped = r.data?.skipped || 0;
      if (queued === 0) {
        toast.info(r.data?.message || 'Allt redan klart 💛');
      } else {
        toast.success(`🎬 Bygger ${queued} video${queued !== 1 ? 'r' : ''}… ~1-2 min/st`);
      }
      // Start polling status
      const poll = async () => {
        try {
          const s = await axios.get(`${API}/timefeed/upgrade-all-status`, { withCredentials: true });
          setUpgradeAllStatus(s.data);
          if ((s.data?.counts?.generating || 0) === 0) {
            clearInterval(upgradeAllPollRef.current);
          }
        } catch {/**/}
      };
      poll();
      upgradeAllPollRef.current = setInterval(poll, 10000);
    } catch (e) {
      toast.error('Kunde inte starta: ' + (e?.response?.data?.detail || ''));
    } finally {
      setUpgradeAllBusy(false);
    }
  };

  const pickTopic = ({ topic, custom }) => {
    stopSpeaking();
    if (custom) {
      setClips([custom]);
      setIdx(0);
      return;
    }
    setTopicFilter(topic);
    setClips([]);
    setIdx(0);
    // Trigger fresh fetch with new topic
    setTimeout(() => fetchNext({ topic }), 100);
  };

  const onVideoReady = (clipId, data) => {
    setClips((cs) => cs.map((c) => c.id === clipId
      ? { ...c, video: { status: 'ready', video_base64: data.video_base64, mime_type: data.mime_type } }
      : c));
  };

  const current = clips[idx];

  return (
    <div className="relative w-full bg-black rounded-2xl overflow-hidden" style={{ height: 'calc(100vh - 220px)', minHeight: '500px' }} data-testid="timefeed-page">
      {/* LLM budget banner — floats above the feed when key is unhealthy */}
      <LlmBudgetBanner />
      {/* Top header (compact) */}
      <div className="absolute top-2 left-2 right-2 z-30 flex items-center gap-2">
        <button
          onClick={() => setPickerOpen(true)}
          data-testid="timefeed-topic-btn"
          className="px-3 py-1.5 rounded-full bg-white/15 backdrop-blur hover:bg-white/25 text-white text-xs font-bold flex items-center gap-1.5"
        >
          <Search className="w-3.5 h-3.5" /> {topicFilter ? topics.find((t) => t.key === topicFilter)?.label || 'Tema' : 'Välj tema'}
        </button>
        <div className="flex-1" />
        <button
          onClick={() => setVoiceOn((v) => { if (v) stopSpeaking(); return !v; })}
          data-testid="timefeed-voice-toggle"
          className="p-2 rounded-full bg-white/15 backdrop-blur hover:bg-white/25 text-white"
          title={voiceOn ? 'Stäng röst' : 'Slå på röst'}
        >
          {voiceOn ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
        </button>
        <button
          onClick={loadLiked}
          data-testid="timefeed-liked-btn"
          className="p-2 rounded-full bg-white/15 backdrop-blur hover:bg-white/25 text-white"
          title="Sparade klipp"
        >
          <Heart className="w-4 h-4" />
        </button>
        <button
          onClick={() => setCapsuleOpen(true)}
          data-testid="timefeed-capsule-btn"
          className="p-2 rounded-full bg-amber-500/80 hover:bg-amber-500 text-white shadow"
          title="Veckans Tidskapsel"
        >
          <Film className="w-4 h-4" />
        </button>
        <button
          onClick={() => setHistoryBookOpen(true)}
          data-testid="timefeed-historybook-btn"
          className="p-2 rounded-full bg-violet-700/85 hover:bg-violet-700 text-white shadow"
          title="Min historiebok — dina sparade resor"
        >
          <BookOpen className="w-4 h-4" />
        </button>
      </div>

      {/* Clip area */}
      <div
        ref={containerRef}
        className="w-full h-full"
        onClick={() => current && setFactsOpen(true)}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
        onWheel={onWheel}
      >
        {!current && loadingNext && (
          <div className="w-full h-full flex flex-col items-center justify-center text-white/80 gap-3 px-6 text-center" data-testid="timefeed-initial-loader">
            <Loader2 className="w-10 h-10 animate-spin text-amber-300" />
            <p className="text-sm italic">Förbereder din första tidsresa…</p>
            <p className="text-[11px] text-white/50">AI ritar scenen + skriver berättarrösten 🎬</p>
            <p className="text-[10px] text-white/40 italic pt-1">Första gången kan ta ~15–25 sek 💛</p>
          </div>
        )}
        {!current && !loadingNext && (
          <div className="w-full h-full flex flex-col items-center justify-center text-white/80 gap-3">
            <p className="text-sm">Ingen klipp än 💛</p>
            <button onClick={() => fetchNext()} className="px-4 py-2 rounded-xl bg-amber-500 text-white font-bold text-sm">
              Försök igen
            </button>
          </div>
        )}
        {current && <ClipCard clip={current} isActive={true} voiceOn={voiceOn} onLike={toggleLike} onVideoReady={onVideoReady} onImmerse={(c) => { stopSpeaking(); setImmerseClipId(c.id); }} onSimulate={(c) => { stopSpeaking(); setSimClipId(c.id); }} />}
      </div>

      {/* Bottom hint + nav arrows */}
      <div className="absolute bottom-3 left-0 right-0 z-20 flex flex-col items-center gap-1 pointer-events-none">
        <p className="text-[10px] text-white/60 italic pointer-events-none">
          Svep upp för nästa · Tryck på bilden för fakta
        </p>
        <div className="flex gap-2 pointer-events-auto">
          <button
            onClick={goPrev}
            disabled={idx === 0}
            data-testid="timefeed-prev"
            className="p-2 rounded-full bg-white/15 backdrop-blur hover:bg-white/30 text-white disabled:opacity-30"
          >
            <ChevronUp className="w-4 h-4" />
          </button>
          <button
            onClick={goNext}
            disabled={loadingNext}
            data-testid="timefeed-next"
            className="p-2 rounded-full bg-white/15 backdrop-blur hover:bg-white/30 text-white disabled:opacity-50"
          >
            {loadingNext ? <Loader2 className="w-4 h-4 animate-spin" /> : <ChevronDown className="w-4 h-4" />}
          </button>
        </div>
      </div>

      {/* Modals */}
      <TopicPicker open={pickerOpen} onClose={() => setPickerOpen(false)} onPick={pickTopic} topics={topics} />
      <FactsDrawer open={factsOpen} clip={current} onClose={() => setFactsOpen(false)} />

      {capsuleOpen && <TimeCapsuleModal onClose={() => setCapsuleOpen(false)} />}

      {immerseClipId && <TimeImmerseModal clipId={immerseClipId} onClose={() => setImmerseClipId(null)} />}

      {simClipId && <TimeSimulatorModal clipId={simClipId} onClose={() => setSimClipId(null)} />}

      {historyBookOpen && <TimeHistoryBookModal onClose={() => setHistoryBookOpen(false)} />}

      {/* Liked overlay */}
      {showLiked && (
        <div className="fixed inset-0 z-[350] bg-black/80 backdrop-blur flex items-start sm:items-center justify-center p-3 sm:p-6 overflow-y-auto" onClick={() => setShowLiked(false)} data-testid="timefeed-liked-modal">
          <div className="bg-gradient-to-br from-amber-50 to-rose-50 rounded-3xl max-w-2xl w-full shadow-2xl border-2 border-amber-200 max-h-[88vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-3 bg-gradient-to-r from-amber-100 to-rose-100 border-b border-amber-200 rounded-t-3xl flex items-center justify-between">
              <h2 className="font-bold text-amber-900 flex items-center gap-2"><Heart className="w-4 h-4 fill-rose-500 text-rose-500" /> Sparade klipp</h2>
              <button onClick={() => setShowLiked(false)} className="p-1.5 rounded-full hover:bg-white/60"><X className="w-5 h-5" /></button>
            </div>
            {likedClips.length > 0 && (
              <div className="px-5 py-2.5 bg-amber-50/60 border-b border-amber-100 flex items-center gap-3 flex-wrap">
                <button
                  onClick={upgradeAllLiked}
                  disabled={upgradeAllBusy || (upgradeAllStatus?.counts?.generating > 0)}
                  data-testid="timefeed-upgrade-all-btn"
                  className="px-3 py-2 rounded-xl bg-gradient-to-r from-violet-500 to-pink-500 hover:from-violet-600 hover:to-pink-600 text-white font-bold text-xs shadow flex items-center gap-1.5 disabled:opacity-60"
                >
                  {upgradeAllBusy || (upgradeAllStatus?.counts?.generating > 0)
                    ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    : <Video className="w-3.5 h-3.5" />}
                  Skapa video för alla
                </button>
                {upgradeAllStatus && (
                  <div className="flex items-center gap-2 text-[11px]" data-testid="timefeed-upgrade-status">
                    {upgradeAllStatus.counts.ready > 0 && (
                      <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-bold">
                        🎬 {upgradeAllStatus.counts.ready} klar
                      </span>
                    )}
                    {upgradeAllStatus.counts.generating > 0 && (
                      <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-bold animate-pulse">
                        ⏳ {upgradeAllStatus.counts.generating} bygger…
                      </span>
                    )}
                    {upgradeAllStatus.counts.failed > 0 && (
                      <span className="px-2 py-0.5 rounded-full bg-rose-100 text-rose-700 font-bold">
                        ⚠️ {upgradeAllStatus.counts.failed} miss
                      </span>
                    )}
                  </div>
                )}
              </div>
            )}
            <div className="overflow-y-auto p-4 grid grid-cols-2 sm:grid-cols-3 gap-3">
              {likedClips.length === 0 && <p className="col-span-full text-center text-amber-700/70 italic py-6">Inga sparade än 💛</p>}
              {likedClips.map((c) => {
                const itemStatus = upgradeAllStatus?.items?.find((i) => i.id === c.id)?.status;
                return (
                <button
                  key={c.id}
                  onClick={() => {
                    setClips([c, ...clips.filter((x) => x.id !== c.id)]);
                    setIdx(0);
                    setShowLiked(false);
                  }}
                  data-testid={`timefeed-liked-${c.id}`}
                  className="aspect-[3/4] rounded-xl overflow-hidden relative shadow border-2 border-white hover:scale-105 transition"
                  style={{
                    backgroundImage: c.image_base64 ? `url("data:${c.image_mime || 'image/png'};base64,${c.image_base64}")` : undefined,
                    backgroundSize: 'cover', backgroundPosition: 'center',
                  }}
                >
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                  {itemStatus === 'ready' && (
                    <span className="absolute top-1.5 right-1.5 bg-emerald-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full flex items-center gap-0.5">
                      <Film className="w-2.5 h-2.5" /> Video
                    </span>
                  )}
                  {itemStatus === 'generating' && (
                    <span className="absolute top-1.5 right-1.5 bg-amber-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full flex items-center gap-0.5 animate-pulse">
                      <Loader2 className="w-2.5 h-2.5 animate-spin" /> Bygger
                    </span>
                  )}
                  <div className="absolute bottom-1.5 left-1.5 right-1.5 text-white text-left">
                    <p className="text-[9px] font-bold text-amber-300">{c.year}</p>
                    <p className="text-xs font-black uppercase leading-tight" style={{ textShadow: '0 2px 8px rgba(0,0,0,0.9)' }}>
                      {c.hook}
                    </p>
                  </div>
                </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes kenburns {
          0% { transform: scale(1.0) translate(0, 0); }
          100% { transform: scale(1.15) translate(-2%, -2%); }
        }
      `}</style>
    </div>
  );
}
